import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  useTheme,
  ListItemButton
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Cancel,
  Work,
  Person,
  EmojiEvents,
  BarChart,
  CalendarToday,
  AccessTime,
  Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { parseUtc } from '../../utils/time';


const AnalyticsDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agingData, setAgingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [dashboardRes, agingRes] = await Promise.all([
        api.get(`/admin/analytics/dashboard?days=${period}`),
        api.get('/admin/reports/job-aging')
      ]);
      setData(dashboardRes.data);
      setAgingData(agingRes.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Failed to load analytics</Typography>
      </Box>
    );
  }

  const { jobs, workers, clients, trends } = data;

  return (
    <Box sx={{ p: 0, pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
            <Typography variant="h5" fontWeight={800} color="text.primary">Analytics</Typography>
            <Typography variant="body2" color="text.secondary">Performance metrics and insights</Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select 
            value={period} 
            onChange={(e) => setPeriod(e.target.value)}
            variant="outlined"
            sx={{ 
                bgcolor: 'white', 
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' }
            }}
          >
            <MenuItem value={7}>Last 7 Days</MenuItem>
            <MenuItem value={14}>Last 14 Days</MenuItem>
            <MenuItem value={30}>Last 30 Days</MenuItem>
            <MenuItem value={90}>Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <MetricCard
            title="Total Jobs"
            value={jobs.total}
            icon={<Work />}
            color={theme.palette.primary.main}
            trend="+5%" // Mock trend
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            title="Completed"
            value={jobs.completed}
            icon={<CheckCircle />}
            color={theme.palette.success.main}
            trend="+12%"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            title="Completion Rate"
            value={`${jobs.completion_rate}%`}
            icon={<TrendingUp />}
            color={theme.palette.info.main}
            trend="+2%"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            title="Active Jobs"
            value={jobs.active}
            icon={<BarChart />}
            color={theme.palette.warning.main}
            trend="Stable"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Status Breakdown */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Job Status Breakdown</Typography>
              {trends.status_breakdown.map((item) => (
                <Box key={item.status} sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">{item.status}</Typography>
                    <Typography variant="body2" fontWeight={700} color="text.primary">{item.count}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(item.count / jobs.total) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#F1F5F9',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getStatusColor(item.status, theme),
                        borderRadius: 5
                      }
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Workers */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                <EmojiEvents sx={{ mr: 1, color: '#F59E0B' }} />
                Top Performers
              </Typography>
              <List disablePadding>
                {workers.top_performers.map((worker, idx) => (
                  <React.Fragment key={worker.id}>
                    {idx > 0 && <Divider component="li" sx={{ my: 1 }} />}
                    <ListItem sx={{ py: 1.5, px: 0 }}>
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: idx === 0 ? '#FEF3C7' : idx === 1 ? '#F1F5F9' : '#FFF7ED',
                            color: idx === 0 ? '#D97706' : idx === 1 ? '#64748B' : '#C2410C',
                            width: 40,
                            height: 40,
                            fontWeight: 700,
                            fontSize: '1rem',
                            border: '1px solid',
                            borderColor: idx === 0 ? '#FDE68A' : idx === 1 ? '#E2E8F0' : '#FFEDD5'
                          }}
                        >
                          {idx + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body1" fontWeight={600}>{worker.name}</Typography>}
                        secondary={<Typography variant="body2" color="text.secondary">{worker.jobs_completed} jobs completed</Typography>}
                      />
                      {idx === 0 && <EmojiEvents sx={{ color: '#F59E0B' }} />}
                    </ListItem>
                  </React.Fragment>
                ))}
                {workers.top_performers.length === 0 && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No performance data available yet
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Aging Report */}
        {agingData && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AccessTime sx={{ mr: 1, color: theme.palette.warning.main }} />
                  <Typography variant="h6" fontWeight={700}>Job Aging Report</Typography>
                </Box>
                
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {Object.entries(agingData.summary).map(([bucket, count]) => {
                    let color = theme.palette.success.main;
                    let label = "0-3 Days";
                    if (bucket === '3-7_days') { color = theme.palette.info.main; label = "3-7 Days"; }
                    if (bucket === '7-14_days') { color = theme.palette.warning.main; label = "7-14 Days"; }
                    if (bucket === '14+_days') { color = theme.palette.error.main; label = "14+ Days"; }
                    
                    return (
                      <Grid item xs={6} md={3} key={bucket}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            textAlign: 'center', 
                            bgcolor: `${color}10`,
                            border: `1px solid ${color}30`,
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="h4" fontWeight={800} sx={{ color: color }}>
                            {count}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {label}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                {agingData.slipping_jobs.length > 0 && (
                  <>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Warning sx={{ fontSize: 18, mr: 1, color: theme.palette.error.main }} />
                      Slipping Jobs (Needs Attention)
                    </Typography>
                    <List disablePadding sx={{ bgcolor: '#FFF5F5', borderRadius: 2, border: '1px solid #FED7D7' }}>
                      {agingData.slipping_jobs.map((job, idx) => (
                        <React.Fragment key={job.id}>
                          {idx > 0 && <Divider component="li" />}
                          <ListItem disablePadding>
                            <ListItemButton onClick={() => navigate(`/jobs/${job.id}`)} sx={{ py: 1.5 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight={600}>
                                    #{job.id} - {job.description}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {job.client} • {job.status} • Created {parseUtc(job.created_at).toLocaleDateString()}
                                  </Typography>
                                }
                              />
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" fontWeight={700} color="error.main">
                                  {job.age_days} days
                                </Typography>
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Daily Trend */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Daily Activity</Typography>
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
                {trends.daily_jobs.map((day) => (
                  <Paper
                    key={day.date}
                    elevation={0}
                    sx={{
                      minWidth: 100,
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 3,
                      bgcolor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      flexShrink: 0
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, textTransform: 'uppercase', fontWeight: 700, fontSize: '0.7rem' }}>
                      {new Date(day.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' })}
                    </Typography>
                    <Box sx={{ my: 1 }}>
                        <Typography variant="h5" color="text.primary" fontWeight={800}>
                        {day.completed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Completed
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #E2E8F0' }}>
                        <Typography variant="caption" color="success.main" fontWeight={600}>
                        +{day.created} new
                        </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const getStatusColor = (status, theme) => {
    const s = status.toLowerCase();
    if (s.includes('complete')) return theme.palette.success.main;
    if (s.includes('progress') || s.includes('route') || s.includes('site')) return theme.palette.info.main;
    if (s.includes('fail') || s.includes('cancel')) return theme.palette.error.main;
    if (s.includes('warn') || s.includes('pending')) return theme.palette.warning.main;
    return theme.palette.primary.main;
};

const MetricCard = ({ title, value, icon, color, trend }) => (
  <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Avatar 
          variant="rounded"
          sx={{ 
            bgcolor: `${color}15`, 
            color: color,
            width: 48, 
            height: 48,
            borderRadius: 2
          }}
        >
          {React.cloneElement(icon, { fontSize: 'medium' })}
        </Avatar>
        {trend && (
            <Box sx={{ bgcolor: '#F0FDF4', px: 1, py: 0.5, borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ fontSize: 14, color: '#16A34A', mr: 0.5 }} />
                <Typography variant="caption" fontWeight={700} color="#166534">
                    {trend}
                </Typography>
            </Box>
        )}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight={800} sx={{ color: 'text.primary', mb: 0.5 }}>
        {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {title}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

export default AnalyticsDashboard;
